import { useCallback } from 'react';

import { useNavigateToEntity } from '@/lib/crm/hooks/useNavigateToEntity';

/**
 * Wire a highlight tile to the entity its value stands for.
 *
 * Highlight tiles that summarise a specific record ("Last emailed" → an email
 * thread, "Last met" / "Next meeting" → a meeting, "Company" → the associated
 * company) all navigate the same way: push the target onto the slideout stack
 * via `useNavigateToEntity`, which works identically from the full-page record
 * and from a slideout. Centralising it here keeps every tile's click behaviour
 * (and its "no target ⇒ no affordance" rule) in one place.
 *
 * @returns a stable click handler, or `null` when there is nothing to open.
 *   Pass the result straight to `HighlightCardShell.onClick`: `null` leaves the
 *   card a static `<div>` — no pointer cursor, no focus stop, no fake button.
 */
export function useOpenHighlightTarget(
  targetEntityId: string | null | undefined,
  targetEntityTypeName?: string
): (() => void) | null {
  const navigateToEntity = useNavigateToEntity();

  const handleOpen = useCallback(() => {
    if (!targetEntityId) {
      return;
    }
    navigateToEntity(targetEntityId, targetEntityTypeName);
  }, [navigateToEntity, targetEntityId, targetEntityTypeName]);

  return targetEntityId ? handleOpen : null;
}
